/**
 * reliabilityUtils.js — Shared reliability mental model
 *
 * One coherent reliability system across the app. Used by:
 *   - PREAnalytics.jsx       (generic Reliability tab)
 *   - HRLabReport.jsx        (PeopleLab "Reliability & Assumptions" panel)
 *
 * Reliability = Method Quality + Response Certainty + Segment Stability + Data Coverage
 *
 * Pillars (always have the same labels and meanings everywhere):
 *   - methodQuality       — outcome mapping confidence, measurement setup, context disclosure
 *   - responseCertainty   — PRE avg_p_max, avg_individual_entropy, model confidence
 *   - segmentStability    — divergence across demographic groups / segment consistency
 *   - coverage            — full-org coverage, valid responses, sample size
 *
 * All scores are 0-100. All levels are 'high' | 'medium' | 'low' | 'unknown'.
 */

// ---------------------------------------------------------------------------
// Level normalization
// ---------------------------------------------------------------------------

export function normalizeReliabilityLevel(level) {
  if (level === 'high') return 'high';
  if (level === 'medium' || level === 'moderate') return 'medium';
  if (level === 'low') return 'low';
  return 'unknown';
}

export function levelToScore(level) {
  const normalized = normalizeReliabilityLevel(level);
  if (normalized === 'high') return 85;
  if (normalized === 'medium') return 60;
  if (normalized === 'low') return 35;
  return null;
}

export function scoreToLevel(score) {
  if (score == null || Number.isNaN(score)) return 'unknown';
  if (score >= 70) return 'high';
  if (score >= 45) return 'medium';
  return 'low';
}

export function getReliabilityTone(level) {
  const normalized = normalizeReliabilityLevel(level);
  if (normalized === 'high') return 'positive';
  if (normalized === 'medium') return 'neutral';
  if (normalized === 'low') return 'negative';
  return 'unknown';
}

export function levelLabel(level) {
  const normalized = normalizeReliabilityLevel(level);
  if (normalized === 'high') return 'High';
  if (normalized === 'medium') return 'Moderate';
  if (normalized === 'low') return 'Low';
  return 'Unknown';
}

// ---------------------------------------------------------------------------
// Pillar computations
// ---------------------------------------------------------------------------

function computeMethodQuality({ peopleLabStability, diagnostics, simulation }) {
  // Prefer explicit mapping confidence from PeopleLab diagnostics
  const mappingTable = diagnostics?.mapping_table || [];
  const confident = mappingTable.filter((m) => m.mapping_status === 'confident').length;
  const total = mappingTable.length;

  // Method quality is also affected by context disclosure
  const contextMode = simulation?.config?.context_mode || simulation?.context_mode;
  const contextHasOrgInfo = contextMode === 'standard' || contextMode === 'light' || contextMode == null;

  if (total > 0) {
    const ratio = confident / total;
    let level;
    let score;
    if (ratio >= 0.9) {
      level = 'high';
      score = 90;
    } else if (ratio >= 0.5) {
      level = 'medium';
      score = 65;
    } else {
      level = 'low';
      score = 40;
    }
    if (!contextHasOrgInfo) score = Math.max(score - 5, 30);
    return {
      level,
      score,
      reason:
        ratio >= 0.9
          ? 'HR outcome mappings confirmed'
          : ratio >= 0.5
          ? 'Most outcome mappings confirmed; some need review'
          : 'Outcome mappings need verification',
    };
  }

  // Fallback to PeopleLab overall stability factor about mapping
  if (peopleLabStability?.factors?.length) {
    const mappingFactor = peopleLabStability.factors.find((f) => /mapping/i.test(f));
    if (mappingFactor) {
      const isConfident = /confident/i.test(mappingFactor);
      const isUnmapped = /unmapped/i.test(mappingFactor);
      const level = isConfident ? 'high' : isUnmapped ? 'low' : 'medium';
      return { level, score: levelToScore(level), reason: mappingFactor };
    }
  }

  return { level: 'unknown', score: null, reason: 'Method quality not available' };
}

function computeResponseCertainty({ preData, peopleLabStability }) {
  // PRE provides direct evidence
  if (preData?.has_pre_data) {
    const avgPMax = preData.avg_p_max || 0;
    const avgEntropy = preData.avg_individual_entropy ?? null;
    // P-Max contributes most: 0.65+ = high agreement, <0.40 = low
    let score;
    if (avgPMax >= 0.6) score = 85;
    else if (avgPMax >= 0.45) score = 65;
    else if (avgPMax >= 0.3) score = 45;
    else score = 30;

    // Entropy-based dampening (higher entropy = lower certainty)
    if (avgEntropy != null) {
      if (avgEntropy > 1.5) score = Math.max(score - 10, 25);
      else if (avgEntropy > 1.0) score = Math.max(score - 5, 30);
    }
    return {
      level: scoreToLevel(score),
      score,
      reason: `Average top-choice probability ${(avgPMax * 100).toFixed(0)}%`,
    };
  }

  // Fallback to PeopleLab stability when PRE not available
  if (peopleLabStability?.level) {
    const level = normalizeReliabilityLevel(peopleLabStability.level);
    return {
      level,
      score: levelToScore(level),
      reason: 'Estimated from response distribution',
    };
  }

  return { level: 'unknown', score: null, reason: 'Response certainty not available' };
}

function computeSegmentStability({ preData, peopleLabStability }) {
  // PRE provides max divergence across demographic segments
  if (preData?.has_pre_data && preData.max_divergence != null) {
    const div = preData.max_divergence;
    let score;
    if (div < 8) score = 85;
    else if (div < 15) score = 65;
    else if (div < 22) score = 45;
    else score = 30;
    return {
      level: scoreToLevel(score),
      score,
      reason: `Max segment divergence ${div.toFixed(1)}pp`,
    };
  }

  // Fallback: PeopleLab stability factors mention segment consistency
  if (peopleLabStability?.factors?.length) {
    const segFactor = peopleLabStability.factors.find((f) => /segment|consistent|variance/i.test(f));
    if (segFactor) {
      let level;
      if (/consistent/i.test(segFactor)) level = 'high';
      else if (/high variance/i.test(segFactor)) level = 'low';
      else level = 'medium';
      return { level, score: levelToScore(level), reason: segFactor };
    }
  }

  if (peopleLabStability?.level) {
    const level = normalizeReliabilityLevel(peopleLabStability.level);
    return { level, score: levelToScore(level), reason: 'Inferred from overall stability' };
  }

  return { level: 'unknown', score: null, reason: 'Segment stability not available' };
}

function computeCoverage({ simulation, diagnostics }) {
  // Census integrity from simulation document
  const isFull = !!simulation?.is_full_census;
  const incomplete = !!simulation?.incomplete_census_flag;
  const coverageRate = simulation?.coverage_rate;
  const actualN = simulation?.actual_n;
  const totalN = simulation?.population_total_n;

  // Sample size from diagnostics
  const sampleSize = diagnostics?.data_quality?.sample_size ?? actualN;
  const validResponses = diagnostics?.data_quality?.valid_responses ?? actualN;

  let score;
  let reason;

  if (incomplete) {
    score = 50;
    reason = `Incomplete census: ${actualN ?? '?'} of ${totalN ?? '?'} processed`;
  } else if (isFull && coverageRate != null && coverageRate >= 0.99) {
    score = 95;
    reason = 'Full workforce census processed';
  } else if (sampleSize != null) {
    if (sampleSize >= 200) {
      score = 90;
      reason = `Strong sample (n=${sampleSize})`;
    } else if (sampleSize >= 100) {
      score = 80;
      reason = `Good sample (n=${sampleSize})`;
    } else if (sampleSize >= 50) {
      score = 60;
      reason = `Moderate sample (n=${sampleSize})`;
    } else if (sampleSize > 0) {
      score = 40;
      reason = `Small sample (n=${sampleSize})`;
    } else {
      score = null;
      reason = 'Sample size unknown';
    }
  } else {
    score = null;
    reason = 'Coverage data not available';
  }

  // Penalize when valid responses are noticeably lower than sample size
  if (validResponses != null && sampleSize && validResponses < sampleSize * 0.85 && score != null) {
    score = Math.max(score - 10, 25);
    reason += ` · ${validResponses}/${sampleSize} valid`;
  }

  return {
    level: score == null ? 'unknown' : scoreToLevel(score),
    score,
    reason,
  };
}

// ---------------------------------------------------------------------------
// Public: build the unified reliability summary
// ---------------------------------------------------------------------------

export function buildReliabilitySummary({
  simulation = null,
  preData = null,
  peopleLabStability = null,
  diagnostics = null,
} = {}) {
  const pillars = {
    methodQuality: computeMethodQuality({ peopleLabStability, diagnostics, simulation }),
    responseCertainty: computeResponseCertainty({ preData, peopleLabStability }),
    segmentStability: computeSegmentStability({ preData, peopleLabStability }),
    coverage: computeCoverage({ simulation, diagnostics }),
  };

  // Aggregate score: weighted average across pillars that have a score
  const weights = {
    methodQuality: 0.25,
    responseCertainty: 0.3,
    segmentStability: 0.25,
    coverage: 0.2,
  };
  let weightedSum = 0;
  let totalWeight = 0;
  for (const [key, pillar] of Object.entries(pillars)) {
    if (pillar.score != null) {
      weightedSum += pillar.score * weights[key];
      totalWeight += weights[key];
    }
  }
  const score = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : null;
  const level = score == null ? 'unknown' : scoreToLevel(score);

  // Warnings: surface problem pillars
  const warnings = [];
  for (const [key, pillar] of Object.entries(pillars)) {
    if (pillar.level === 'low') {
      warnings.push({ pillar: key, message: pillar.reason });
    }
  }
  if (simulation?.incomplete_census_flag) {
    warnings.push({
      pillar: 'coverage',
      message:
        'Intended to simulate the full workforce but fewer employees were processed — interpret segment-level findings with caution.',
    });
  }

  // Plain-English summary
  let summary;
  if (level === 'high') {
    summary =
      'Results are stable enough for broad interpretation. Method, response certainty, segment stability and coverage all hold up.';
  } else if (level === 'medium') {
    summary =
      'Results are directionally useful, but some pillars are weaker. Pay attention to segment differences and assumptions before acting.';
  } else if (level === 'low') {
    summary =
      'Results are sensitive — at least one reliability pillar is weak. Validate with additional scenarios or real-world data before deciding.';
  } else {
    summary = 'Not enough information to assess reliability for this run.';
  }

  return {
    level,
    score,
    label: levelLabel(level),
    summary,
    pillars,
    warnings,
  };
}

// ---------------------------------------------------------------------------
// Pillar metadata for UI rendering
// ---------------------------------------------------------------------------

export const PILLAR_META = {
  methodQuality: {
    title: 'Method Quality',
    description: 'Outcome mapping, measurement setup, and context disclosure.',
  },
  responseCertainty: {
    title: 'Response Certainty',
    description: 'How confident agents were in their answers (P-max, individual entropy).',
  },
  segmentStability: {
    title: 'Segment Stability',
    description: 'How consistent results are across demographic groups.',
  },
  coverage: {
    title: 'Data Coverage',
    description: 'Sample size and full-organization census integrity.',
  },
};

export const PILLAR_ORDER = ['methodQuality', 'responseCertainty', 'segmentStability', 'coverage'];
